// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Third-parties.
import TableCSVExport from '../../../../component/TableCSVExport';

// Types.
import InstanceTarget from '../../../../types/instance/InstanceTarget';

// Helper.
import { getDefaultPageSize } from '../../../../helpers/utils';
import api from "../../../../api/Base";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../../helpers/timeRangeUtils";

// Components.
import ReactTable from '../../../../component/ReactTable';
import Alert from '../../../../component/Alert';
import SearchInput from '../../../../component/SearchInput';

export default function Jobs(props: {
    instance: InstanceTarget,
    showByDefault: boolean,
    jobsData?: any[],
    jobNameFilter?: string
}) {
    const { instance, showByDefault, jobsData, jobNameFilter } = props;
    const timeRangeContext = useContext(TimeRangeContext);

    const [loading, setLoading] = useState<boolean>(true);
    const [jobs, setJobs] = useState<any[]>([]);

    const [tableFilter, setTableFilter] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current && showByDefault) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, [showByDefault]);

    void useMemo(async () => {
        try {
            if (jobsData && jobsData.length > 0) {
                if (jobNameFilter) {
                    const filtered = jobsData.filter(job => job.jobname === jobNameFilter);
                    setJobs(filtered);
                } else {
                    setJobs(jobsData);
                }
                setLoading(false);
                return;
            }

            setLoading(true);
            const jobNameParam = jobNameFilter ? `&jobname=${encodeURIComponent(jobNameFilter)}` : '';
            const response = await api.get(`job/history/job?${buildApiQuery(timeRangeContext.timeRange)}&id=${instance.id}${jobNameParam}`);
            if (response.data && Array.isArray(response.data)) {
                setJobs(response.data);
            } else {
                setJobs([]);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to retrieve job data:', error);
            setJobs([]);
            setLoading(false);
        }
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, instance.id, instance.type, jobsData, jobNameFilter]);

    const filteredJobs = useMemo(() =>
                    jobs.filter((job) => {
                        const jobName = job.jobname || 'N/A';

                        return jobName.toLowerCase().includes(tableFilter.toLowerCase());
                    }).map((job, index) => ({
                        ...job,
                        _rowIndex: index
                    })),
            [jobs, tableFilter]);

    const columns: Column[] = useMemo(() => [
        {
            Header: 'Job Name',
            accessor: 'jobname',
            canSort: true,
            Cell: ({ value }: { value: string }) => value || 'N/A'
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({ value }: { value: number }) => value?.toLocaleString() || '0'
        },
        {
            Header: 'Succeeded',
            accessor: 'succeeded',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({ value }: { value: number }) => (
                    <span className="text-success">
                    {value?.toLocaleString() || '0'}
                </span>
            )
        },
        {
            Header: 'Failed',
            accessor: 'failed',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({ value }: { value: number }) => (
                    <span className={value > 0 ? 'text-danger' : ''}>
                    {value?.toLocaleString() || '0'}
                </span>
            )
        },
        {
            Header: 'Success Rate',
            accessor: 'successrate',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({ value }: { value: number }) => {
                const rate = value || 0;
                const colorClass = rate === 100 ? 'text-success' : rate >= 90 ? 'text-warning' : rate > 0 ? 'text-danger' : '';
                return (
                        <span className={colorClass}>
                        {rate}%
                    </span>
                );
            }
        },
        {
            Header: 'Last Execution',
            accessor: 'maxtimeslice',
            canSort: true,
            Cell: ({ value }: { value: string }) => {
                return value ? new Date(value).toLocaleString() : 'N/A';
            }
        }
    ], []);

    const tableInstance = useTable(
            {
                columns: columns,
                data: filteredJobs,
                initialState: {
                    pageIndex: 0,
                    pageSize: getDefaultPageSize(),
                    sortBy: [{ id: 'executions', desc: true }]
                },
                autoResetPage: false,
                autoResetSortBy: false,
                autoResetFilters: false,
                autoResetSelectedRows: false,
                autoResetExpanded: false,
                autoResetGroupBy: false,
            },
            useSortBy,
            usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter, gotoPage]);

    function clearTableFilter() {
        setTableFilter('');
    }

    return (
            <div id="jobs" className={`tab-pane fade ${showByDefault ? 'show active' : ''}`} role="tabpanel" aria-labelledby="jobs-tab">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-briefcase fa-fw" aria-hidden="true"/>
                        Jobs
                        <span className="badge bg-info"
                              data-tooltip-id="global-tooltip" data-tooltip-content="Filtered job count">{filteredJobs.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseJobs" aria-expanded="false" aria-controls="collapseJobs"/>
                    </div>
                    <div id="collapseJobs" className="card-body collapse show">
                        {Number(process.env.REACT_APP_API_LIMIT) - 1 === jobs.length - 1 && (
                                <Alert
                                        message={`To help preserve performance, we limit the total number of job records in the table below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1}. The summary widgets above show totals for all jobs in the selected time period, not just the visible records.`}
                                        heading="Job records" variant="alert-info"/>
                        )}
                        <div className="row table-search align-items-center">
                            <div className="col-auto ms-auto">
                                <TableCSVExport
                                    tableInstance={tableInstance}
                                    filename="SQL Agent Jobs Summary"
                                />
                            </div>
                            <div className="col-md-2">
                                <SearchInput
                                    ref={inputRef}
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                />
                            </div>
                        </div>
                        <ReactTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            dataLength={filteredJobs.length}
                            error={null}
                            loading={loading}
                            getRowProps={() => ({ className: 'table-row' })}
                        />
                    </div>
                </div>
            </div>
    );
}

